import { ReactNode, useState, MouseEvent, useEffect } from "react"
import Select from 'react-select';
import { ReactComponent as DoubleChevronLeft } from '../../assets/Icons/chevron/Double-Chevron-Left.svg';
import { ReactComponent as DoubleChevronRight } from '../../assets/Icons/chevron/Double-Chevron-Right.svg';
import { ReactComponent as ChevronLeft } from '../../assets/Icons/chevron/Chevron-Left.svg';
import { ReactComponent as ChevronRight } from '../../assets/Icons/chevron/Chevron-Right.svg';
import { Skeleton, useTheme } from "@mui/material";

const paginationLimitOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: 'All', label: 'All'},
] 

export default function UpdatedPagination({
    count,
    limit,
    offset,
    setPaginationLimit,
    setPaginationOffset,
    size,
    isLoading,
}: {
    count: number,
    limit: number,
    offset: number,
    setPaginationLimit: (limit: number) => void,
    setPaginationOffset: (pageNumber: number) => void,
    size: 'sm' | 'reg',
    isLoading: boolean,
}) {
    const theme = useTheme();
    const [ btns, setBtns ] = useState<{
        icon: ReactNode,
        action: (e:MouseEvent<HTMLButtonElement>) => void,
        isDisabled: boolean,
    }[]>([]);

    const handleLimitChange = (e: any, currentCount: number) => {
        const value = e.value as string;

        let updatedLimit = 0;

        if (value === 'All') {
            updatedLimit = currentCount;
        } else {
            updatedLimit = Number(value);
        }

        setPaginationLimit(updatedLimit);

    }


    useEffect(() => {
            setBtns([
                {
                    icon: <DoubleChevronLeft />,
                    action: (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setPaginationOffset(0);
                    },
                    isDisabled: offset === 0 ? true : false,
                },
                {
                    icon: <ChevronLeft />,
                    action: (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setPaginationOffset((offset/limit)-1);
                    },
                    isDisabled: offset === 0 ? true : false,
                },
                {
                    icon: <ChevronRight />,
                    action: (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setPaginationOffset((offset/limit)+1);
                    },
                    isDisabled: offset + limit >= count ? true : false,
                },
                {
                    icon: <DoubleChevronRight />,
                    action: (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setPaginationOffset(Math.ceil(count/limit)-1)
                    },
                    isDisabled: offset + limit >= count ? true : false,
                },
            ])

             //eslint-disable-next-line
        }, [offset, count, limit]);
    



    return (
        <div className='flex justify-end rounded-bl rounded-br'>
            <div className='flex justify-end items-center rounded gap-10'>
                <div className='flex justify-center items-center gap-3 text-default'>
                    <p className={`text-nowrap text-default ${size === 'sm' ? 'text-[12px]' : 'text-[14px]'}`}>Results per page</p>
                    <Select 
                        menuPlacement='top' 
                        styles={{
                            control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderColor: state.isFocused ? theme.palette.primary.main : '#C1C7CD',
                                width: '100%',
                                height: size === 'sm' ? 30 : 40,
                                minHeight: 0,
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    cursor: 'pointer'
                                },
                                fontSize: size === 'sm' ? '12px' : 'inherit',
                            }),
                            valueContainer: (baseStyles, state) => ({
                                ...baseStyles,
                                padding: '0 12px',
                            }),
                            option: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: state.isSelected ? theme.palette.secondary.main : state.isFocused ? theme.palette.secondary.light : 'white',
                                color: '#35424A',
                                '&:hover': {
                                    cursor: 'pointer',
                                },
                                fontSize: size === 'sm' ? '12px' : 'inherit',
                            }),
                            dropdownIndicator: (baseStyles, state) => ({
                                ...baseStyles,
                                padding: size === 'sm' ? '0px 8px' : baseStyles.padding,
                            }),

                        }}
                        aria-label='pagination-selection' 
                        options={paginationLimitOptions}
                        onChange={(e:any) => handleLimitChange(e, count)}
                        value={{ value: limit.toString(), label: limit === count ? 'All' : limit.toString() }}
                    />
                </div>
                {isLoading ? (
                    <Skeleton variant="text" style={{ fontSize: size === 'sm' ? '12px' : '14px' }} width={90} /> 
                ) : (
                    <p className={`text-nowrap text-default ${size === 'sm' ? 'text-[12px]' : 'text-[14px]'}`}>{offset}-{count - offset <= limit ? count : offset + limit} of {count}</p>
                )}
                <div className='flex justify-center items-center gap-3'>
                    {btns.map((btn, i) => (
                        <button 
                            key={i}
                            onClick={btn.action} 
                            disabled={btn.isDisabled} 
                            className={`bg-white border ${btn.isDisabled ? 'hover:text-default hover:border-outline opacity-50' : 'hover:text-primary hover:border-primary opacity-100'} text-default transition-all border-outline ${size === 'sm' ? 'p-2' : 'p-3'} rounded-lg flex justify-center items-center`}
                        >
                            <div className={`${size === 'sm' ? 'w-[14px] h-[14px]' : 'w-[18px] h-[18px]'}`}>{btn.icon}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}